import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, UserCog } from "lucide-react";
import { esEmail } from "@/lib/validacionEs";
import { LEN } from "@/lib/limites";

const I = String.fromCharCode(0xed);
const O = String.fromCharCode(0xf3);
const A = String.fromCharCode(0xe1);
const U = String.fromCharCode(0xfa);
const DASH = "\u2014";

const ROL_LABEL = {
  admin: "Administrador",
  empresa: "Empresa",
  gestoria: "Gestor" + I + "a",
  conductor: "Conductor",
};

const EMPTY = {
  nombre: "",
  email: "",
  password: "",
  password_confirm: "",
  rol: "empresa",
  codigo_empresa: "",
};

function claveEmp(u) {
  return String(u?.codigo_empresa || u?.empresa_id || "").trim().toUpperCase() || "_SIN_";
}

function TablaUsuarios({ titulo, grupos, vacio, onEdit, onRemove, plano }) {
  const n = grupos.reduce((acc, g) => acc + g.users.length, 0);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div>
          <div className="text-sm font-semibold text-slate-900">{titulo}</div>
          <div className="text-xs text-slate-500">{n} usuario{n === 1 ? "" : "s"}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="deca-table w-full min-w-[720px]">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Usuario</th>
              <th>Empresa</th>
              <th>{"C" + O + "digo"}</th>
              <th>Rol</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {grupos.map((g) => (
              <React.Fragment key={g.key}>
                {plano ? null : (
                <tr className="bg-slate-100" data-testid={`usuarios-grupo-${g.key}`}>
                  <td colSpan={6} className="py-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-sm font-semibold text-slate-900">{g.nombre || DASH}</span>
                      {g.codigo ? <span className="font-mono text-xs text-slate-500">{g.codigo}</span> : null}
                      <span className="text-xs text-slate-500">{g.users.length} usuario{g.users.length === 1 ? "" : "s"}</span>
                    </div>
                  </td>
                </tr>
                )}
                {g.users.map((u) => (
                  <tr key={u.id} data-testid={`usuario-row-${u.id}`}>
                    <td className="font-medium">{u.nombre || DASH}</td>
                    <td className="text-sm">{u.email}</td>
                    <td className="text-sm">{u.empresa_nombre || g.nombre || DASH}</td>
                    <td className="font-mono text-xs">{u.codigo_empresa || u.empresa_id || DASH}</td>
                    <td><span className="status-badge status-borrador">{ROL_LABEL[u.rol] || u.rol}</span></td>
                    <td className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-2" onClick={() => onEdit(u)} aria-label="Editar usuario" data-testid={`btn-edit-${u.id}`}>
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true"/>
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-700" onClick={() => onRemove(u)} aria-label="Eliminar usuario" data-testid={`btn-del-${u.id}`}>
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true"/>
                      </Button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {n === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-500">
                  <UserCog className="h-8 w-8 mx-auto text-slate-400 mb-2"/>
                  {vacio}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function UsuariosPage() {
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [q, setQ] = useState("");
  const [filtroEmp, setFiltroEmp] = useState("todas");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: users }, { data: emps }] = await Promise.all([
        api.get("/api/v1/usuarios"),
        api.get("/api/v1/empresas"),
      ]);
      setItems(users.items || []);
      setEmpresas(emps.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const filtrados = useMemo(() => {
    let list = items;
    if (filtroEmp && filtroEmp !== "todas") {
      const f = String(filtroEmp).toUpperCase();
      list = list.filter((u) => claveEmp(u) === f);
    }
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((u) =>
      [u.nombre, u.email, u.rol, ROL_LABEL[u.rol], u.codigo_empresa || u.empresa_id, u.empresa_nombre].join(" ").toLowerCase().includes(s)
    );
  }, [items, q, filtroEmp]);

  const grupos = useMemo(() => {
    const map = new Map();
    for (const u of filtrados) {
      if (u.rol === "admin") continue;
      const k = claveEmp(u);
      if (!map.has(k)) {
        const emp = empresas.find((e) => String(e.codigo || "").toUpperCase() === k);
        map.set(k, {
          key: k,
          codigo: k === "_SIN_" ? "" : k,
          nombre: emp?.nombre || u.empresa_nombre || (k === "_SIN_" ? "Sin empresa" : k),
          demo: emp ? !!emp.demo : (k.startsWith("DEMO") || k === "EMP-1"),
          users: [],
        });
      }
      map.get(k).users.push(u);
    }
    const list = [...map.values()];
    list.sort((a, b) => {
      if (a.key === "_SIN_") return 1;
      if (b.key === "_SIN_") return -1;
      if (!!a.demo !== !!b.demo) return a.demo ? 1 : -1;
      return String(a.nombre || a.codigo).localeCompare(String(b.nombre || b.codigo), "es");
    });
    for (const g of list) {
      g.users.sort((a, b) => String(a.nombre || a.email || "").localeCompare(String(b.nombre || b.email || ""), "es"));
    }
    return list;
  }, [filtrados, empresas]);
  const gruposProd = useMemo(() => grupos.filter((g) => !g.demo), [grupos]);
  const gruposDemo = useMemo(() => grupos.filter((g) => g.demo), [grupos]);
  const gruposAdmin = useMemo(() => {
    const users = filtrados
      .filter((u) => u.rol === "admin")
      .sort((a, b) => String(a.nombre || a.email || "").localeCompare(String(b.nombre || b.email || ""), "es"));
    if (!users.length) return [];
    return [{ key: "_ADMIN_", codigo: "", nombre: "Administradores", demo: false, users }];
  }, [filtrados]);

  const empresasFiltro = useMemo(() => {
    const list = [...empresas];
    list.sort((a, b) => {
      if (!!a.demo !== !!b.demo) return a.demo ? 1 : -1;
      return String(a.nombre || a.codigo).localeCompare(String(b.nombre || b.codigo), "es");
    });
    return list;
  }, [empresas]);

  const opcionesEmpresa = useMemo(() => {
    const list = [...empresas];
    const actual = (form.codigo_empresa || "").trim();
    if (actual && !list.some((e) => (e.codigo || "").toUpperCase() === actual.toUpperCase())) {
      list.push({ id: "tmp-" + actual, codigo: actual, nombre: actual });
    }
    return list;
  }, [empresas, form.codigo_empresa]);

  const openNew = () => {
    setEdit(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEdit(u);
    setForm({
      nombre: u.nombre || "",
      email: u.email || "",
      password: "",
      password_confirm: "",
      rol: u.rol || "empresa",
      codigo_empresa: u.codigo_empresa || u.empresa_id || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Indica el nombre"); return; }
    if (!form.email.trim() || !esEmail(form.email)) {
      toast.error("El usuario debe ser un email v" + String.fromCharCode(0xe1) + "lido");
      return;
    }
    if (!edit && !form.password.trim()) {
      toast.error("La contrase" + String.fromCharCode(0xf1) + "a es obligatoria");
      return;
    }
    if (form.password && form.password.length < 8) {
      toast.error("La contrase" + String.fromCharCode(0xf1) + "a debe tener al menos 8 caracteres");
      return;
    }
    if (form.password || form.password_confirm) {
      if (form.password !== form.password_confirm) {
        toast.error("Las contrase" + String.fromCharCode(0xf1) + "as no coinciden");
        return;
      }
    }
    const codigo = (form.codigo_empresa || "").trim();
    if (!codigo) {
      toast.error("Selecciona la empresa");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        email: form.email.trim(),
        rol: form.rol,
        codigo_empresa: codigo,
        empresa_id: codigo,
      };
      if (form.password.trim()) payload.password = form.password;
      if (edit) await api.patch(`/api/v1/usuarios/${edit.id}`, payload);
      else {
        payload.password = form.password;
        await api.post("/api/v1/usuarios", payload);
      }
      setOpen(false);
      await load();
      toast.success(edit ? "Usuario actualizado" : "Usuario creado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    const ok = window.confirm("Eliminar el usuario " + (u.email || u.nombre) + "?");
    if (!ok) return;
    try {
      await api.delete(`/api/v1/usuarios/${u.id}`);
      await load();
      toast.success("Usuario eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Usuarios"
        subtitle={"Elige la empresa del cat" + A + "logo. Los l" + I + "mites y Sage se heredan de esa empresa."}
        testid="page-usuarios"
        actions={
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="btn-usuario-nuevo">
            <Plus className="h-4 w-4 mr-2"/> Nuevo usuario
          </Button>
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative max-w-md flex-1 min-w-[16rem]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400"/>
            <Input
              className="pl-8"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={"Buscar nombre, email, rol o c" + O + "digo"}
              aria-label="Buscar usuarios"
              data-testid="usuarios-buscar"
            />
          </div>
          <div className="w-full sm:w-72">
            <Label htmlFor="usuarios-filtro-emp" className="label-tag">Empresa</Label>
            <Select value={filtroEmp} onValueChange={setFiltroEmp}>
              <SelectTrigger id="usuarios-filtro-emp" className="mt-1" data-testid="usuarios-filtro-empresa">
                <SelectValue placeholder="Todas las empresas"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las empresas</SelectItem>
                {empresasFiltro.map((e) => (
                  <SelectItem key={e.id || e.codigo} value={e.codigo}>
                    {(e.nombre && e.nombre !== e.codigo ? e.nombre + " (" + e.codigo + ")" : e.codigo)
                      + (e.demo ? " · demo" : "")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        <TablaUsuarios
          titulo="Administradores"
          grupos={gruposAdmin}
          plano
          vacio={items.length === 0 ? "No hay administradores." : "No hay administradores con ese filtro."}
          onEdit={openEdit}
          onRemove={remove}
        />
        <TablaUsuarios
          titulo={"Producci" + O + "n"}
          grupos={gruposProd}
          vacio={items.length === 0 ? "No hay usuarios de producci" + O + "n." : "No hay usuarios de producci" + O + "n con ese filtro."}
          onEdit={openEdit}
          onRemove={remove}
        />
        <TablaUsuarios
          titulo="Demo"
          grupos={gruposDemo}
          vacio={items.length === 0 ? "No hay usuarios de empresas demo." : "No hay usuarios demo con ese filtro."}
          onEdit={openEdit}
          onRemove={remove}
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col overflow-hidden gap-4">
          <DialogHeader className="shrink-0">
            <DialogTitle>{edit ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 space-y-3">
            <Field label="Nombre *">
              <Input className="mt-1" maxLength={LEN.nombre} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="usu-nombre"/>
            </Field>
            <Field label="Usuario (email) *">
              <Input className="mt-1" type="email" maxLength={LEN.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="off" data-testid="usu-email"/>
            </Field>
            <div>
              <Label htmlFor="usu-empresa" className="label-tag">{"Empresa *"}</Label>
              {empresas.length === 0 ? (
                <div className="text-sm text-slate-500 mt-1">{"Crea primero una empresa en el men" + U + " Empresas."}</div>
              ) : (
                <Select value={form.codigo_empresa || undefined} onValueChange={(v) => setForm({ ...form, codigo_empresa: v })}>
                  <SelectTrigger id="usu-empresa" className="mt-1" data-testid="usu-codigo-empresa">
                    <SelectValue placeholder={"Selecciona empresa"}/>
                  </SelectTrigger>
                  <SelectContent>
                    {opcionesEmpresa.map((e) => (
                      <SelectItem key={e.id || e.codigo} value={e.codigo}>
                        {(e.nombre && e.nombre !== e.codigo) ? (e.nombre + " (" + e.codigo + ")") : e.codigo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Field label={edit ? "Contrase" + String.fromCharCode(0xf1) + "a (vac" + I + "o = no cambiar)" : "Contrase" + String.fromCharCode(0xf1) + "a *"}>
              <Input className="mt-1" type="password" maxLength={LEN.password} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" data-testid="usu-password"/>
            </Field>
            <Field label={edit ? "Confirmar contrase" + String.fromCharCode(0xf1) + "a" : "Confirmar contrase" + String.fromCharCode(0xf1) + "a *"}>
              <Input className="mt-1" type="password" maxLength={LEN.password} value={form.password_confirm} onChange={(e) => setForm({ ...form, password_confirm: e.target.value })} autoComplete="new-password" data-testid="usu-password-confirm"/>
            </Field>
            {(form.password || form.password_confirm) && form.password !== form.password_confirm ? (
              <div className="text-[11px] text-red-600" data-testid="usu-password-mismatch">
                Las contrase{String.fromCharCode(0xf1)}as no coinciden.
              </div>
            ) : null}
            <div>
              <Label htmlFor="usu-rol" className="label-tag">Rol</Label>
              <Select value={form.rol} onValueChange={(v) => setForm({ ...form, rol: v })}>
                <SelectTrigger id="usu-rol" data-testid="usu-rol"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROL_LABEL).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t pt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={busy} data-testid="usu-guardar">
              {busy ? "Guardando" + String.fromCharCode(0x2026) : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
